// Bubble text content for Mission 1 with highlights, keyboard layout conditionals, and fellow emotion.
// Bubble size is automatic based on text length.
// Optional per-page bubble layout overrides:
// - bubbleLeftPercent / bubbleBottomPercent
// - bubbleMinWidthPercent / bubbleMaxWidthPercent
// Optional per-page fellow emotion:
// - fellowEmotion: 'hq-happy' | 'hq-impressed' | 'hq-neutral' | 'hq-speaking'
// Each entry: { text: string, highlights: Array<{text?: string, conditional?: object}> }

const DEFAULT_LAYOUT_LABELS = {
 KeyZ: "Z",
 Slash: "/",
 KeyS: "S",
 KeyD: "D",
 KeyF: "F",
 KeyK: "K",
 KeyL: "L",
 Semicolon: ";",
};

export const MISSION_ONE_BUBBLE_TEXT = [
 // 1
 {
  text: `Awesome work!\n\nYour next mission is ready.`,
  audioSrc: "assets/audio/instructions 1.01.mp3",
  fellowEmotion: "hq-happy",
  highlights: [],
 },
 // 2
 {
  text: `Just like before, the first step is to approach targets.\n\n  • Tap the "{{KeyZ}}" key to slow down\n  • Tap the "{{Slash}}" key to speed up\n\nWhen close enough, press the space bar with your thumb to lock on to the target. The car will then appear.`,
  audioSrc: "assets/audio/instructions 1.02.mp3",
  fellowEmotion: "hq-speaking",
  highlights: [
   { text: "key" },
   { text: "slow down" },
   { text: "key" },
   { text: "speed up" },
   { text: "space bar" },
   { text: "thumb" },
   { text: "lock on" },
   { conditional: { key: "KeyZ" } },
   { conditional: { key: "Slash" } },
  ],
 },
 // 3
 {
  text: `This time, the car won't tell you its mafia type.\n\nYou'll need to figure it out by noticing the car's look and behavior.\n\nOver time, you'll learn which cars belongs to which mafia.`,
  audioSrc: "assets/audio/instructions 1.03.mp3",
  fellowEmotion: "hq-impressed",
  highlights: [
   { text: "you'll learn which cars belongs to which mafia." },
  ],
 },
 // 4
 {
  text: `Once you decide the mafia type:\n\n  • Look at the code in the gray window on top.\n  • Focus on the correct part of the code (depending on mafia).\n  • Example: If you think it's Brightness mafia, look only at the background.\n  &nbsp  Decide if it's Dark or Bright.\n  • Then move to the matching lane. I'll explain next how to do this.`,
  audioSrc: "assets/audio/instructions 1.04.mp3",
  fellowEmotion: "hq-speaking",
  highlights: [
   { text: "gray window" },
   { text: "Brightness mafia" },
   { text: "background" },
  ],
 },
 // 5
 {
  text: `Each answer has its own lane and key as shown above.\n\nMove into the right lane to collect the package.\nFamiliarize yourself with the mapping shown above.\n\nDuring the game, icons at the bottom of the screen will remind you of the mapping.`,
  audioSrc: "assets/audio/instructions 1.05.mp3",
  fellowEmotion: "hq-neutral",
  highlights: [
   { text: "Each answer" },
   { text: "own lane and key" },
  ],
 },
 // 6
 {
  text: `Once you move to a lane, the package appears in the correct lane.\n\nSo, the package's lane tells you the correct answer and the car's mafia type.\n\nAn icon above the car will remind you of the correct mafia type (B=Brightness, N=Number, L=Letter).`,
  audioSrc: "assets/audio/instructions 1.06.mp3",
  fellowEmotion: "hq-impressed",
  highlights: [
   { text: "package" },
   { text: "correct lane" },
  ],
 },
 // 7
 {
  text: `You'll also get feedback from the motorcycle driver:\n\n  • The driver is happy if you did everything correctly.\n  • The driver is sad if you chose the wrong mafia.\n  • The driver is surprised if you solved the code incorrectly (wrong category) but you chose the correct mafia type.\n\n You'll also hear different sounds when the driver is happy, sad, or surprised.`,
  audioSrc: "assets/audio/instructions 1.07.mp3",
  fellowEmotion: "hq-happy",
  highlights: [
   { text: "happy" },
   { text: "correctly" },
   { text: "sad" },
   { text: "wrong mafia" },
   { text: "surprised" },
   { text: "code incorrectly (wrong category)" },
   { text: "correct mafia type" },
  ],
 },
 // 8
 {
  text: `From time to time, I'll check in with you.\n\nA set of emoticons will appear.\nChoose the one that best matches how you feel in that moment.\nThe corresponding key will be shown under each icon.`,
  audioSrc: "assets/audio/instructions 2.5.mp3",
  fellowEmotion: "hq-impressed",
  highlights: [
   { text: "emoticons" },
   { text: "how you feel" },
   { text: "key" },
   { text: "under each icon" },
  ],
 },
 // 9
 {
  text: `Some keyboard tips:\n\n • Rest your fingers on the keys as shown above.\n • To make a response, press keys down without lifting your fingers. Tap, don't hold keys down.\n • You can adjust speed when approaching cars AND packages in your lane.\n • To slow down: Tap (don't hold) the {{KeyZ}} key.\n • To speed up: Tap (don't hold) the {{Slash}} key.`,
  audioSrc: "assets/audio/instructions 1.08.mp3",
  fellowEmotion: "hq-speaking",
  highlights: [
   { text: "fingers" },
   { text: "without lifting your fingers" },
   { text: "adjust speed" },
   { text: "approaching cars AND packages" },
   { text: "slow down" },
   { text: "speed up" },
   { conditional: { key: "KeyZ" } },
   { conditional: { key: "Slash" } },
  ],
 },
 // 10
 {
  text: `You can go back and review if you'd like.\n\nOnce you continue, the mission will begin and these messages will disappear.\n\nTip: While in a lane, you can adjust speed by tapping {{Slash}} to go faster or {{KeyZ}} to slow down.`,
  audioSrc: "assets/audio/instructions 1.09.mp3",
  fellowEmotion: "hq-happy",
  highlights: [
   { text: "adjust speed" },
  ],
 },
];

function buildLabelsFromLayoutConfig(layoutConfig) {
 return {
  ...DEFAULT_LAYOUT_LABELS,
  KeyZ: layoutConfig?.backwardSlowDownKey ?? DEFAULT_LAYOUT_LABELS.KeyZ,
  Slash: layoutConfig?.forwardSpeedUpKey ?? DEFAULT_LAYOUT_LABELS.Slash,
  KeyS: layoutConfig?.KeyS ?? DEFAULT_LAYOUT_LABELS.KeyS,
  KeyD: layoutConfig?.KeyD ?? DEFAULT_LAYOUT_LABELS.KeyD,
  KeyF: layoutConfig?.KeyF ?? DEFAULT_LAYOUT_LABELS.KeyF,
  KeyK: layoutConfig?.KeyK ?? DEFAULT_LAYOUT_LABELS.KeyK,
  KeyL: layoutConfig?.KeyL ?? DEFAULT_LAYOUT_LABELS.KeyL,
  Semicolon: layoutConfig?.Semicolon ?? DEFAULT_LAYOUT_LABELS.Semicolon,
 };
}

export function getKeyLabel(key, layoutConfig) {
 const labels = buildLabelsFromLayoutConfig(layoutConfig);
 return labels[key] || key;
}
